import axios, { AxiosRequestConfig } from "axios";
import type {
  LoginResponse,
  Location,
  MfaMethod,
  MfaVerifyResponse,
  Vacation,
  Worker,
  WorkingHours,
} from "./types";

// Resolution order:
//   1. Local-dev build → always hit the local backend.
//   2. Runtime config injected by `public/runtime-config.js` (the container's
//      entrypoint rewrites it from $API_URL at startup).
//   3. Build-time fallback so a fresh deploy without runtime config still works.
const runtimeApiUrl =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.apiUrl) || "";

const API_URL = process.env.REACT_APP_LOCAL_BUILD === "true"
  ? "http://localhost:8000"
  : runtimeApiUrl || "https://app.sessionhoster.com";

export { API_URL };

export const TURNSTILE_SITE_KEY =
  (typeof window !== "undefined" && window.__APP_CONFIG__?.turnstileSiteKey) ||
  "0x4AAAAAADQj0Xb4NBR1ZPDR";

const client = axios.create({ baseURL: `${API_URL}/api`, withCredentials: true });

export const UNAUTHORIZED_EVENT = "auth:unauthorized";

function _fireUnauthorized() {
  localStorage.removeItem("token");
  window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}

// When an authenticated request comes back 401:
//   1. Try to silently refresh the access token via the httpOnly refresh cookie.
//   2. If the refresh succeeds, update localStorage and retry the original request.
//   3. If the refresh itself fails, clear the token and fire UNAUTHORIZED_EVENT.
let _refreshing = false;
let _refreshQueue: ((token: string) => void)[] = [];

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const reqStatus = error?.response?.status;
    const hadAuth = Boolean(error?.config?.headers?.Authorization);
    const isRefreshCall = error?.config?.url?.includes("/auth/refresh");

    if (reqStatus === 401 && hadAuth && !isRefreshCall && localStorage.getItem("token")) {
      const originalRequest = error.config;

      if (_refreshing) {
        return new Promise((resolve) => {
          _refreshQueue.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(client(originalRequest));
          });
        });
      }

      _refreshing = true;
      try {
        const res = await client.post("/auth/refresh");
        const newToken = res.data.access_token;
        localStorage.setItem("token", newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        _refreshQueue.forEach((cb) => cb(newToken));
        _refreshQueue = [];
        return client(originalRequest);
      } catch {
        _refreshQueue = [];
        _fireUnauthorized();
      } finally {
        _refreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Kept for tests that import it directly.
export function handleUnauthorizedResponse(error: any): Promise<never> {
  const reqStatus = error?.response?.status;
  const hadAuth = Boolean(error?.config?.headers?.Authorization);
  if (reqStatus === 401 && hadAuth && localStorage.getItem("token")) {
    _fireUnauthorized();
  }
  return Promise.reject(error);
}

// Helper for authenticated calls — pass the token in and get back a config
// object that the request methods accept directly.
const auth = (token: string, extra: AxiosRequestConfig = {}): AxiosRequestConfig => ({
  ...extra,
  headers: { ...(extra.headers || {}), Authorization: `Bearer ${token}` },
});

// --- Auth -----------------------------------------------------------------

export async function loginWorker(email: string, password: string): Promise<LoginResponse> {
  const res = await client.post("/login", { e_mail: email, password });
  return { mfa_required: false as const, ...res.data };
}

export async function logoutWorker(): Promise<void> {
  try {
    await client.post("/logout");
  } catch {
    // Best-effort — if the server is unreachable we still clear local state.
  }
}

export async function registerWorker(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  cfTurnstileToken: string,
): Promise<{ message?: string; detail?: string }> {
  // Returns the error body on 4xx so callers can render server-side validation
  // messages without having to unwrap an axios error.
  try {
    const res = await client.post("/register", {
      first_name: firstName,
      last_name: lastName,
      e_mail: email,
      password,
      cf_turnstile_token: cfTurnstileToken,
    });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
}

export async function getWorker(token: string): Promise<Worker> {
  const res = await client.get("/verify", auth(token));
  return res.data;
}

export async function verifyMfa(
  mfaToken: string,
  code: string,
  method: MfaMethod,
): Promise<MfaVerifyResponse> {
  const res = await client.post("/mfa/verify", { mfa_token: mfaToken, code, method });
  return res.data;
}

export async function requestMfaEmailCode(mfaToken: string) {
  const res = await client.post("/mfa/email-code", { mfa_token: mfaToken });
  return res.data;
}

export async function verifyWorkerEmail(token: string) {
  const res = await client.post(`/workers/email-verify/${token}`);
  return res.data;
}

export async function resendEmailVerification(email: string) {
  const res = await client.post("/email-verify/resend", { e_mail: email });
  return res.data;
}

export async function requestPasswordReset(email: string, turnstileToken: string) {
  const res = await client.post("/password-reset/request", { e_mail: email, cf_turnstile_token: turnstileToken });
  return res.data;
}

export async function performPasswordReset(token: string, password: string) {
  const res = await client.post(`/password-reset/${token}`, { password });
  return res.data;
}

export async function requestPasswordChange(
  currentPassword: string,
  newPassword: string,
  token: string,
) {
  const res = await client.post(
    "/password-change/request",
    { current_password: currentPassword, new_password: newPassword },
    auth(token),
  );
  return res.data;
}

export async function confirmPasswordChange(token: string) {
  const res = await client.post(`/password-change/${token}`);
  return res.data;
}

export async function confirmEmailChange(token: string) {
  const res = await client.post(`/profile/email-change/${token}`);
  return res.data;
}

// --- Booking (customer-facing) -------------------------------------------

export async function getWorkerAvailability(
  workerPublicId: string,
  month: string,
  duration: number = 1.0,
  locationId: number | string | null = null,
  rescheduleToken: string | null = null,
) {
  const params: Record<string, unknown> = { month, duration };
  if (locationId != null) params.location = locationId;
  if (rescheduleToken != null) params.reschedule_token = rescheduleToken;
  const res = await client.get(`/workers/${workerPublicId}`, { params });
  return res.data;
}

export async function createAppointment(workerPublicId: string, appointmentData: unknown) {
  const res = await client.post(`/workers/${workerPublicId}/appointments`, appointmentData);
  return res.data;
}

export async function confirmAppointment(token: string) {
  const res = await client.post(`/appointments/confirm/${token}`);
  return res.data;
}

export async function fetchCancelPreview(token: string) {
  const res = await client.get(`/appointments/cancel/${token}`);
  return res.data;
}

export async function cancelAppointmentCustomer(token: string) {
  const res = await client.post(`/appointments/cancel/${token}`);
  return res.data;
}

// --- Working hours --------------------------------------------------------

export async function fetchWorkingHours(token: string): Promise<WorkingHours[]> {
  const res = await client.get("/working-hours", auth(token));
  return res.data;
}

export async function updateWorkingHours(
  token: string,
  day: string,
  start_hour: number,
  end_hour: number,
) {
  const res = await client.put("/working-hours", { day, start_hour, end_hour }, auth(token));
  return res.data;
}

export async function removeWorkingHours(token: string, day: string) {
  await client.delete("/working-hours", auth(token, { data: { day } }));
}

// --- Appointments (artist-facing) ----------------------------------------

export type GetAppointmentsOptions = {
  showDeclined?: boolean;
  page?: number;
  pageSize?: number;
  month?: number | null;
  year?: number | null;
};

export async function getAppointments(
  token: string,
  {
    showDeclined = false,
    page = 1,
    pageSize = 5,
    month = null,
    year = null,
  }: GetAppointmentsOptions = {},
) {
  const params: Record<string, unknown> = {
    show_declined: showDeclined,
    page,
    page_size: pageSize,
  };
  if (month != null && year != null) {
    params.month = month + 1;
    params.year = year;
  }
  const res = await client.get("/appointments", auth(token, { params }));
  return res.data;
}

export async function getNewAppointments(token: string) {
  const res = await client.get("/appointments/new", auth(token));
  return res.data.new_appointments;
}

export async function exportAppointmentsIcs(token: string): Promise<void> {
  const res = await client.get("/appointments/export.ics", auth(token, { responseType: "blob" }));
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "appointments.ics";
  a.click();
  URL.revokeObjectURL(url);
}

export async function getCalendarToken(token: string): Promise<string> {
  const res = await client.get("/profile/calendar-token", auth(token));
  return res.data.calendar_token;
}

export async function regenerateCalendarToken(token: string): Promise<string> {
  const res = await client.post("/profile/calendar-token/regenerate", {}, auth(token));
  return res.data.calendar_token;
}

export async function updateAppointmentStatus(
  appointmentId: number,
  status: string,
  token: string,
) {
  const res = await client.put(`/appointments/${appointmentId}/status`, { status }, auth(token));
  return res.data;
}

export async function rescheduleAppointment(
  appointmentId: number,
  payload: unknown,
  token: string,
) {
  const res = await client.put(`/appointments/${appointmentId}`, payload, auth(token));
  return res.data;
}

export async function cancelAppointmentArtist(appointmentId: number, token: string) {
  const res = await client.post(`/appointments/${appointmentId}/cancel`, {}, auth(token));
  return res.data;
}

export async function submitRescheduleRequest(
  cancelToken: string,
  payload: { day: string; start_time: string },
) {
  const res = await client.post(`/appointments/reschedule-request/${cancelToken}`, payload);
  return res.data;
}

export async function acceptRescheduleRequest(appointmentId: number, token: string) {
  const res = await client.post(`/appointments/${appointmentId}/reschedule-request/accept`, {}, auth(token));
  return res.data;
}

export async function declineRescheduleRequest(appointmentId: number, token: string) {
  const res = await client.post(`/appointments/${appointmentId}/reschedule-request/decline`, {}, auth(token));
  return res.data;
}

export async function updateAppointmentDetails(
  appointmentId: number,
  payload: unknown,
  token: string,
) {
  const res = await client.patch(`/appointments/${appointmentId}/details`, payload, auth(token));
  return res.data;
}

// --- Vacations ------------------------------------------------------------

export async function getVacations(token: string): Promise<Vacation[]> {
  const res = await client.get("/vacations", auth(token));
  return res.data;
}

export async function createVacation(vacationData: unknown, token: string): Promise<Vacation> {
  const res = await client.post("/vacations", vacationData, auth(token));
  return res.data;
}

export async function updateVacation(
  vacationId: number,
  vacationData: unknown,
  token: string,
): Promise<Vacation> {
  const res = await client.put(`/vacations/${vacationId}`, vacationData, auth(token));
  return res.data;
}

export async function deleteVacation(vacationId: number, token: string) {
  await client.delete(`/vacations/${vacationId}`, auth(token));
}

// --- Profile --------------------------------------------------------------

export async function updateWorkerProfile(profileData: unknown, token: string) {
  const res = await client.put("/profile", profileData, auth(token));
  return res.data;
}

export async function updateWorkerLang(lang: string, token: string) {
  const res = await client.put("/profile/lang", { lang }, auth(token));
  return res.data;
}

export async function updateDurationOptions(durationOptions: number[], token: string) {
  const res = await client.put(
    "/profile/duration-options",
    { duration_options: durationOptions },
    auth(token),
  );
  return res.data;
}

export async function updateCustomPublicId(customPublicId: string, token: string) {
  const res = await client.put(
    "/profile/custom-public-id",
    { custom_public_id: customPublicId },
    auth(token),
  );
  return res.data;
}

export async function updateDefaultDuration(defaultDuration: number, token: string) {
  const res = await client.put(
    "/profile/default-duration",
    { default_duration: defaultDuration },
    auth(token),
  );
  return res.data;
}

export async function updateBookingAdvanceDays(days: number, token: string) {
  const res = await client.put(
    "/profile/booking-advance-days",
    { booking_advance_days: days },
    auth(token),
  );
  return res.data;
}

export async function updateBreakBetweenAppointments(minutes: number, token: string) {
  const res = await client.put(
    "/profile/break-between-appointments",
    { break_between_appointments: minutes },
    auth(token),
  );
  return res.data;
}

export async function uploadLogo(file: File, token: string): Promise<{ logo_url: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await client.post("/profile/logo", form, auth(token, { headers: { "Content-Type": "multipart/form-data" } }));
  return res.data;
}

export async function deleteLogo(token: string): Promise<{ logo_url: null }> {
  const res = await client.delete("/profile/logo", auth(token));
  return res.data;
}

export async function updateBio(bio: string | null, token: string): Promise<{ bio: string | null }> {
  const res = await client.put("/profile/bio", { bio }, auth(token));
  return res.data;
}

// --- Locations ------------------------------------------------------------

export async function getLocations(token: string): Promise<Location[]> {
  const res = await client.get("/locations", auth(token));
  return res.data;
}

export async function createLocation(payload: unknown, token: string): Promise<Location> {
  const res = await client.post("/locations", payload, auth(token));
  return res.data;
}

export async function updateLocation(
  locationId: number,
  payload: unknown,
  token: string,
): Promise<Location> {
  const res = await client.put(`/locations/${locationId}`, payload, auth(token));
  return res.data;
}

export async function deleteLocation(locationId: number, token: string) {
  await client.delete(`/locations/${locationId}`, auth(token));
}

// --- Recurring closures ---------------------------------------------------

export type RecurringClosure = {
  id: number;
  weekday: number;
  weekday_name: string;
  interval_weeks: number;
  reference_date: string;
  label: string | null;
};

export async function getRecurringClosures(token: string): Promise<RecurringClosure[]> {
  const res = await client.get("/recurring-closures", auth(token));
  return res.data;
}

export async function createRecurringClosure(
  weekday: number,
  interval_weeks: number,
  reference_date: string,
  token: string,
): Promise<RecurringClosure> {
  const res = await client.post("/recurring-closures", { weekday, interval_weeks, reference_date }, auth(token));
  return res.data;
}

export async function deleteRecurringClosure(id: number, token: string): Promise<void> {
  await client.delete(`/recurring-closures/${id}`, auth(token));
}

// --- MFA management (logged-in) ------------------------------------------

export async function getMfaStatus(token: string) {
  const res = await client.get("/profile/mfa", auth(token));
  return res.data;
}

export async function dismissMfaSetupPrompt(token: string) {
  const res = await client.post("/profile/mfa/dismiss-setup-prompt", {}, auth(token));
  return res.data;
}

export async function setupTotpMfa(token: string) {
  const res = await client.post("/profile/mfa/totp/setup", {}, auth(token));
  return res.data;
}

export async function enableTotpMfa(token: string, code: string) {
  const res = await client.post("/profile/mfa/totp/enable", { code }, auth(token));
  return res.data;
}

export async function enableEmailMfa(token: string) {
  const res = await client.post("/profile/mfa/email/enable", {}, auth(token));
  return res.data;
}

export async function disableEmailMfa(token: string, password: string) {
  const res = await client.delete("/profile/mfa/email", auth(token, { data: { password } }));
  return res.data;
}

export async function disableTotpMfa(token: string, password: string) {
  const res = await client.delete("/profile/mfa/totp", auth(token, { data: { password } }));
  return res.data;
}

// --- Google Calendar -----------------------------------------------------

export type GoogleStatus = {
  enabled: boolean;
  connected: boolean;
  account_email: string | null;
  calendar_id: string | null;
  connected_at: string | null;
  missing_scopes: string[];
};

export async function getGoogleStatus(token: string): Promise<GoogleStatus> {
  const res = await client.get("/google/status", auth(token));
  return res.data;
}

export async function startGoogleOAuth(token: string): Promise<{ authorization_url: string }> {
  const res = await client.get("/google/oauth/start", auth(token));
  return res.data;
}

export async function completeGoogleOAuth(code: string, state: string) {
  const res = await client.post("/google/oauth/callback", { code, state });
  return res.data;
}

export async function disconnectGoogle(token: string) {
  const res = await client.delete("/google/connection", auth(token));
  return res.data;
}

export type GoogleCalendar = {
  id: string;
  summary: string;
  primary: boolean;
  background_color?: string | null;
};

export async function listGoogleCalendars(
  token: string,
): Promise<{ selected_calendar_id: string; calendars: GoogleCalendar[] }> {
  const res = await client.get("/google/calendars", auth(token));
  return res.data;
}

export async function setGoogleCalendar(token: string, calendarId: string) {
  const res = await client.put("/google/calendar", { calendar_id: calendarId }, auth(token));
  return res.data;
}
